import React, { useEffect, useState } from 'react';
import { AnimatedNumber } from '../../common/AnimationUtils';
import PulseBeacon from '../sections/PulseBeacon';
import './ScoreCard.css';

/**
 * ScoreCard
 * One score ring for every dashboard. The ring is large and centred, and the
 * percent, rating label and one-line caption all sit INSIDE the ring.
 *
 * Props
 *   eyebrow      small tracking label above the ring, e.g. "completeness score"
 *   score        0..100 (null/undefined renders a no-data state)
 *   label        rating word inside the ring, e.g. "Good"
 *   caption      one short line under the label, e.g. "Minor gaps detected"
 *   color        ring colour; defaults to green >= 85, amber >= 70, red below
 *   severity     'critical' | 'warning' | null  (drives PulseBeacon; derived from score if omitted)
 *   isDarkMode
 *   className    extra class for section hover rules (e.g. "cps-score-card")
 *   size         ring diameter in px (default 168; shrinks on small screens via CSS)
 *   startDelay   ms before the ring sweeps in (let the card float in first)
 *   suffix       text after the number (default "%")
 *   decimals     decimals on the number (default 0)
 */
const ScoreCard = ({
  eyebrow = 'quality score',
  score,
  label = '',
  caption = '',
  color = null,
  severity,
  isDarkMode = false,
  className = '',
  size = 168,
  startDelay = 300,
  suffix = '%',
  decimals = 0,
  style = {},
}) => {
  const noData = score === null || score === undefined || Number.isNaN(score);
  const safe = noData ? 0 : Math.max(0, Math.min(100, score));

  const ringColor = color || (noData
    ? (isDarkMode ? '#6B7280' : '#9CA3AF')
    : safe >= 85 ? (isDarkMode ? '#34d399' : '#047857')
      : safe >= 70 ? '#D97706'
        : (isDarkMode ? '#f87171' : '#b91c1c'));

  const sev = severity !== undefined
    ? severity
    : (noData ? null : safe < 70 ? 'critical' : safe < 85 ? 'warning' : null);

  // viewBox is fixed at 120; the ring scales with CSS width so it stays responsive
  const VB = 120;
  const STROKE = 6;
  const r = (VB - STROKE) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (safe / 100) * circumference;

  const [ready, setReady] = useState(false);
  useEffect(() => {
    let raf1 = null;
    let raf2 = null;
    const t = setTimeout(() => {
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setReady(true));
      });
    }, Math.max(0, startDelay));
    return () => {
      clearTimeout(t);
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [startDelay]);

  return (
    <div
      className={`nsx-metric-card dq-score-card ${isDarkMode ? 'dq-score-dark' : ''} ${ready ? 'dq-score-on' : ''} ${className}`}
      style={{ '--dq-score-size': `${size}px`, '--dq-score-color': ringColor, ...style }}
    >
      <PulseBeacon severity={sev} isDarkMode={isDarkMode} />

      {eyebrow && (
        <div className="dq-score-eyebrow">
          <span className="dq-score-dot" />
          {eyebrow}
        </div>
      )}

      <div className="dq-score-ring">
        <svg viewBox={`0 0 ${VB} ${VB}`} className="dq-score-svg" aria-hidden="true">
          <circle
            cx={VB / 2} cy={VB / 2} r={r}
            fill="none"
            className="dq-score-track"
            strokeWidth={STROKE}
          />
          {!noData && (
            <circle
              cx={VB / 2} cy={VB / 2} r={r}
              fill="none"
              stroke={ringColor}
              strokeWidth={STROKE}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={ready ? offset : circumference}
              transform={`rotate(-90 ${VB / 2} ${VB / 2})`}
              className="dq-score-progress"
              style={{ filter: `drop-shadow(0 0 4px ${ringColor}59)` }}
            />
          )}
        </svg>

        <div className="dq-score-inner">
          <div className="dq-score-value" style={{ color: ringColor }}>
            {noData
              ? '\u2014'
              : <AnimatedNumber value={safe} decimals={decimals} suffix={suffix} duration={1100} />}
          </div>
          <div className="dq-score-label">{noData ? 'No data' : label}</div>
          {caption && <div className="dq-score-caption">{caption}</div>}
        </div>
      </div>
    </div>
  );
};

export default ScoreCard;